import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Mail, ArrowLeft, CheckCircle, Loader2 } from "lucide-react";
import { usePasswordReset } from "../../../hooks/usePasswordReset";

const PasswordResetRequest = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const { handleRequestPasswordReset } = usePasswordReset();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleChange = (e) => {
    setEmail(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await handleRequestPasswordReset(email);
    } catch (error) {
      console.error("Password reset request error:", error);
    }
    
    setIsSubmitting(false);
    setIsSuccess(true);
    setFeedbackMessage(t("password_reset_feedback", "If an account exists with this email, you will receive a password reset link."));

    setTimeout(() => {
      setEmail("");
      setFeedbackMessage("");
      navigate("/login");
    }, 4000);
  };

  const handleBack = () => {
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-3 sm:p-4">
      <motion.div
        className="w-full max-w-md"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl p-5 sm:p-8">
          {/* Header */}
          <div className="text-center mb-6 sm:mb-8">
            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
              <Mail className="w-7 h-7 sm:w-8 sm:h-8 text-primary" />
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1.5 sm:mb-2">
              {t("password_reset_title", "Reset Password")}
            </h1>
            <p className="text-gray-600 text-xs sm:text-sm px-2">
              {t("password_reset_description", "Enter your email address and we'll send you a link to reset your password.")}
            </p>
          </div>

          {/* Success Message */}
          {isSuccess && feedbackMessage && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 bg-green-50 rounded-xl mb-4 sm:mb-6"
            >
              <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs sm:text-sm text-green-800 font-medium">{feedbackMessage}</p>
            </motion.div>
          )}

          {/* Form */}
          {!isSuccess && (
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
              <div className="space-y-1.5 sm:space-y-2">
                <label htmlFor="email" className="block text-xs sm:text-sm font-semibold text-gray-700">
                  {t("emailAddress", "Email Address")}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={email}
                  onChange={handleChange}
                  required
                  placeholder={t("enter_email", "Enter your email")}
                  className="w-full px-3 sm:px-4 py-3 sm:py-3.5 text-sm sm:text-base border-2 border-gray-200 rounded-xl
                             bg-white text-gray-900 placeholder-gray-400
                             focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20
                             transition-all duration-200"
                />
              </div>

              <motion.button
                type="submit"
                disabled={isSubmitting || !email}
                className={`
                  w-full py-3 sm:py-3.5 px-4 sm:px-6 rounded-xl font-semibold text-white text-sm sm:text-base
                  transition-all duration-200 flex items-center justify-center gap-2
                  ${isSubmitting || !email
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-primary hover:bg-primary/90 active:scale-[0.98] shadow-lg'
                  }
                `}
                whileHover={!isSubmitting && email ? { scale: 1.01 } : undefined}
                whileTap={!isSubmitting && email ? { scale: 0.98 } : undefined}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                    <span>{t("sending", "Sending...")}</span>
                  </>
                ) : (
                  t("send_reset_link", "Send Reset Link")
                )}
              </motion.button>
            </form>
          )}

          {/* Back to Login */}
          <button
            type="button"
            onClick={handleBack}
            className="w-full mt-3 sm:mt-4 flex items-center justify-center gap-1.5 sm:gap-2 py-2.5 sm:py-3 px-4 
                       text-gray-600 hover:text-gray-900 font-medium text-xs sm:text-sm transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span>{t("back_to_login", "Back to Login")}</span>
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default PasswordResetRequest;
